import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    updateOrderRequest,
    updateOrderStatusRequest,
    resetOrderState, downloadOrderPdfRequest
} from "../../../store/order/actions/order-actions";
import {useTranslation} from "react-i18next";
import CustomerAutoComplete from '../../customers/components/CustomerAutoComplete';
import ProductSnapshotsSection from "../../../components/ProductSnapshotsSection";
import {AllowedOrderStatus} from "../OrderStatus";
import {toast} from "react-toastify";
import {useLocation, useNavigate} from "react-router-dom";
import { fetchAllCurrencies, getBankClients, getAvailableDatesForBankClient } from '../../../services/currency/currency-service';
import { saveProformaInvoice, downloadProformaInvoicesAllZip } from '../../../services/order/order-service';

const StatusBadge = ({ status }) => {
    const statusStyles = {
        OPEN: 'bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300',
        IN_PROGRESS: 'bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-yellow-900 dark:text-yellow-300',
        DONE: 'bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300',
        CLOSED: 'bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-red-900 dark:text-red-300'
    };

    return (
        <span className={`${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
            {status.replace('_', ' ')}
        </span>
    );
};

const getDisplayName = (customer) => {
    return customer ? (customer.name || `${customer.firstName} ${customer.lastName}`) : '';
};

const getUserFullName = (userId, users) => {
    if (!userId || !users) return 'N/A';
    const user = users.find(u => u.id === userId);
    if (user) {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || 'N/A';
    }
    return 'N/A';
};

const OrderDetailsSection = ({order, error}) => {
    const { t } = useTranslation();

    const getBankClient = (order) => {
        if(!order) {
            return '';
        }
        if(order.currencyInfo) {
            return order.currencyInfo.bankClient;
        }
        if(order.customCurrency) {
            return 'CUSTOM';
        }
    }

    const formatPrice = (price, currency) => {
        if (!price && price !== 0) return 'N/A';
        const formattedNumber = parseFloat(price).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        const currencySymbol = currency === 'EUR' ? '€' : currency === 'RSD' ? 'RSD' : currency || '';
        return `${formattedNumber} ${currencySymbol}`;
    };

    const dispatch = useDispatch();
    const isOrderDone = order?.status === 'DONE' || order?.status === 'CLOSED';
    const users = useSelector(state => state.user?.users || []);
    const [customerName, setCustomerName] = useState('');
    const [customerId, setCustomerId] = useState(order?.customerId ?? '');
    const [currency, setCurrency] = useState(order?.currency ?? 'EUR');
    const [existingSnapshots, setExistingSnapshots] = useState(order?.productSnapshots ?? []);
    const [orderStatus, setOrderStatus] = useState(order?.status ?? 'OPEN');
    const [bankClient, setBankClient] = useState(getBankClient(order));
    const [currencyHistoryId, setCurrencyHistoryId] = useState(order?.currencyHistoryId ?? null);
    const [overrideForCompany, setOverrideForCompany] = useState(false);
    const [forCompanyValue, setForCompanyValue] = useState(order?.forCompany || '');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [showUserSuggestions, setShowUserSuggestions] = useState(false);
    const [customCurrency, setCustomCurrency] = useState(
        order?.customCurrency ?? {
            bankName: '',
            fromCurrency: 'RSD',
            toCurrency: 'EUR',
            amount: 0.00
        });


    const [validityOfTheOffer, setValidityOfTheOffer] = useState(order?.additionalInfo?.validityOfTheOffer ?? '');
    const [deliveryTimeFrom, setDeliveryTimeFrom] = useState(order?.additionalInfo?.deliveryTimeFrom ?? '');
    const [deliveryTimeTo, setDeliveryTimeTo] = useState(order?.additionalInfo?.deliveryTimeTo ?? '');
    const [placeOfDelivery, setPlaceOfDelivery] = useState(order?.additionalInfo?.placeOfDelivery ?? '');
    const [warranty, setWarranty] = useState(order?.additionalInfo?.warranty ?? '');

    // Currency-related state
    const [currencies, setCurrencies] = useState([]);
    const [availableBankClients, setAvailableBankClients] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Handle forCompany input change with user suggestions
    const handleForCompanyChange = (e) => {
        const value = e.target.value;
        setForCompanyValue(value);
        
        if (value.trim()) {
            const filtered = users.filter(user => {
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                const searchTerm = value.toLowerCase();
                return fullName.toLowerCase().includes(searchTerm) || 
                       (user.username && user.username.toLowerCase().includes(searchTerm)) ||
                       (user.email && user.email.toLowerCase().includes(searchTerm));
            });
            setFilteredUsers(filtered);
            setShowUserSuggestions(filtered.length > 0);
        } else {
            setFilteredUsers([]);
            setShowUserSuggestions(false);
        }
    };

    const handleUserSelect = (user) => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        setForCompanyValue(fullName || user.username || user.email);
        setShowUserSuggestions(false);
        setFilteredUsers([]);
    };

    // Initialize forCompany fields when order loads or changes
    useEffect(() => {
        if (order?.forCompany) {
            setOverrideForCompany(true);
            setForCompanyValue(order.forCompany);
        } else {
            setOverrideForCompany(false);
            setForCompanyValue('');
        }
        setShowUserSuggestions(false);
        setFilteredUsers([]);
    }, [order?.id, order?.forCompany]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showUserSuggestions && !event.target.closest('.relative')) {
                setShowUserSuggestions(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showUserSuggestions]);

    // Load currencies on component mount
    useEffect(() => {
        const loadCurrencies = async () => {
            setIsLoadingCurrencies(true);
            try {
                const json = await fetchAllCurrencies();
                const data = json.data;
                setCurrencies(data);
                const clients = getBankClients(data);
                setAvailableBankClients(clients);
            } catch (error) {
                console.error('Error loading currencies:', error);
                toast.error('Failed to load currency data');
            } finally {
                setIsLoadingCurrencies(false);
            }
        };

        loadCurrencies();
    }, []);

    // Update form when order changes
    useEffect(() => {
        if (order) {
            setCustomerId(order.customerId ?? '');
            setCurrency(order.currency ?? 'EUR');
            setExistingSnapshots(order.productSnapshots ?? []);
            setCustomerName(getDisplayName(order.customer) ?? '');
            setOrderStatus(order.status ?? 'OPEN');
            setBankClient(getBankClient(order));
            setCurrencyHistoryId(order.currencyInfo?.id ?? null);
            setCustomCurrency({
                bankName: order.customCurrency?.bankName ?? '',
                fromCurrency: order.customCurrency?.fromCurrency ?? 'RSD',
                toCurrency: order.customCurrency?.toCurrency ?? 'EUR',
                amount: order.customCurrency?.amount ?? 0.00
            });

            setValidityOfTheOffer(order.additionalInfo?.validityOfTheOffer ?? '');
            setDeliveryTimeFrom(order.additionalInfo?.deliveryTimeFrom ?? '');
            setDeliveryTimeTo(order.additionalInfo?.deliveryTimeTo ?? '');
            setPlaceOfDelivery(order.additionalInfo?.placeOfDelivery ?? '');
            setWarranty(order.additionalInfo?.warranty ?? '');
        }
    }, [order]);

    // Update available dates when bank client changes
    useEffect(() => {
        if (bankClient && bankClient !== 'CUSTOM' && currencies.length > 0) {
            const dates = getAvailableDatesForBankClient(currencies, bankClient);
            setAvailableDates(dates);
            
            // If there's a currencyHistoryId but it's not in the available dates, reset it
            if (currencyHistoryId && !dates.find(d => d.id === currencyHistoryId)) {
                setCurrencyHistoryId(null);
            }
        } else {
            setAvailableDates([]);
            if (bankClient === 'CUSTOM') {
                setCurrencyHistoryId(null);
            }
        }
    }, [bankClient, currencies, currencyHistoryId]);


    const handleOrderDetailsUpdate = useCallback(() => {
        if (!order?.id) return;

        if (bankClient === 'CUSTOM') {
            if (!customCurrency.amount || customCurrency.amount < 0.01) {
                toast.error('You must add currency amount')
                return;
            }
            if (!customCurrency.bankName || customCurrency.bankName.trim() === '') {
                toast.error('You must add bank name')
                return;
            }
        } else if (bankClient && bankClient !== 'CUSTOM') {
            // For non-custom bank clients, require a currency date selection
            if (!currencyHistoryId) {
                toast.error('Please select a currency date for the selected bank client')
                return;
            }
        }

        const originalBankClient = getBankClient(order);
        const originalCurrencyHistoryId = order.currencyInfo?.id ?? null;
        const originalCustomerId = order.customerId ?? '';

        const updatedData = {
            customerId: customerId !== originalCustomerId ? {value: customerId} : undefined,
            currency: currency !== (order.currency ?? null) ? {value: currency} : undefined,
            bankClient: bankClient !== originalBankClient ? {value: bankClient} : undefined,
            currencyHistoryId: currencyHistoryId !== originalCurrencyHistoryId ? {value: currencyHistoryId} : undefined,
        };

        // Handle customCurrency separately so we only send when it actually changed or needs clearing
        const wasCustomBankClient = originalBankClient === 'CUSTOM';
        const isCustomBankClient = bankClient === 'CUSTOM';

        if (isCustomBankClient) {
            const originalCustom = order.customCurrency || {};
            const hasChanged =
                (customCurrency.bankName || '').trim() !== (originalCustom.bankName || '').trim() ||
                Number(customCurrency.amount || 0) !== Number(originalCustom.amount || 0);

            if (hasChanged) {
                updatedData.customCurrency = {
                    value: {
                        fromCurrency: 'RSD',
                        toCurrency: 'EUR',
                        bankName: customCurrency.bankName,
                        amount: customCurrency.amount,
                    },
                };
            }
        } else if (wasCustomBankClient && !isCustomBankClient) {
            // We switched from CUSTOM to a non-custom bank client -> clear customCurrency on backend
            updatedData.customCurrency = {value: null};
        }

        const additionalInfoUpdates = {};

        if (validityOfTheOffer !== (order.additionalInfo?.validityOfTheOffer || '')) {
            additionalInfoUpdates.validityOfTheOffer = {value: validityOfTheOffer};
        }
        if (deliveryTimeFrom !== (order.additionalInfo?.deliveryTimeFrom || '')) {
            additionalInfoUpdates.deliveryTimeFrom = {value: deliveryTimeFrom};
        }
        if (deliveryTimeTo !== (order.additionalInfo?.deliveryTimeTo || '')) {
            additionalInfoUpdates.deliveryTimeTo = {value: deliveryTimeTo};
        }
        if (placeOfDelivery !== (order.additionalInfo?.placeOfDelivery || '')) {
            additionalInfoUpdates.placeOfDelivery = {value: placeOfDelivery};
        }
        if (warranty !== (order.additionalInfo?.warranty || '')) {
            additionalInfoUpdates.warranty = {value: warranty};
        }

        if (Object.keys(additionalInfoUpdates).length > 0) {
            updatedData.additionalInfo = additionalInfoUpdates;
        }

        // Handle forCompany field
        if (overrideForCompany) {
            if (forCompanyValue !== (order.forCompany || '')) {
                updatedData.forCompany = { value: forCompanyValue || null };
            }
        } else {
            if (order.forCompany) {
                updatedData.forCompany = { value: null };
            }
        }

        const filteredData = Object.fromEntries(
            Object.entries(updatedData).filter(([_, v]) => v !== undefined)
        );

        if (Object.keys(filteredData).length > 0) {
            dispatch(updateOrderRequest(order.id, filteredData));
        }
    }, [order,
        customerId, currency,
        bankClient, currencyHistoryId, validityOfTheOffer,
        overrideForCompany, forCompanyValue,
        deliveryTimeFrom, deliveryTimeTo,
        placeOfDelivery, warranty, customCurrency, dispatch]);

    const handleOrderStatusChange = (e) => {
        const newStatus = e.target.value;
        console.log(order)
        if (newStatus === 'IN_PROGRESS') {
            if (!(order?.customer
                && order?.productSnapshots?.length > 0
                && order?.currency)) {
                toast.error("For IN_PROGRESS status, order must have saved customer, saved currency and to have at least one saved product.")
                return;
            }
        }
        setOrderStatus(newStatus);
        if (order?.id) {
            dispatch(updateOrderStatusRequest(order.id, newStatus));
        }
    };

    const handleCloseOrder = () => {
        console.log('handleCloseOrder')
        dispatch(resetOrderState)
        navigate('/orders');
    };

    const handleSaveProformaInvoice = async () => {
        try {
            await saveProformaInvoice(order.id);
        } catch (error) {
            console.error('Error saving proforma invoice:', error);
            toast.error('Failed to save proforma invoice');
        }
    };

    const handleDownloadProformaInvoicesZip = async () => {
        try {
            await downloadProformaInvoicesAllZip(order.id);
        } catch (error) {
            console.error('Error downloading proforma invoices:', error);
            toast.error('Failed to download proforma invoices');
        }
    };

    const setCustomerInfo = (customer) => {
        if (customer) {
            setCustomerId(customer.id);
            setCustomerName(getDisplayName(customer));
        }
    };

    // getDisplayName is now defined at the module level

    const allowedStatuses = AllowedOrderStatus[order.status] || [];

    const statusColors = {
        OPEN: "bg-gray-300 text-gray-700",
        IN_PROGRESS: "bg-yellow-300 text-yellow-800",
        DONE: "bg-green-500 text-white",
        CLOSED: "bg-red-500 text-white"
    };

    return (
        <div className="bg-gray-100 p-6 rounded-lg shadow-md">
            <div className="relative bg-white p-6 shadow-lg rounded-lg">
                <button
                    onClick={handleCloseOrder}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all"
                    title="Close"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
                <h2 className="text-2xl font-semibold text-[#34495e] mb-4">{t('order.orderDetails')}</h2>

                <div className="flex flex-col lg:flex-row gap-10">
                    {/* Left column - General info */}
                    <div className="flex-1">
                        <p className="text-gray-700 mb-2">
                            <strong>{t('order.title')} ID:</strong> {order.id}
                        </p>
                        <p className="text-gray-700 mb-2">
                            <strong>{t('common.createdBy')}:</strong> {order.createdByFullName}
                        </p>
                        <p className="text-gray-700 mb-2">
                            <strong>{t('common.createdAt')}:</strong> {order.createdAt}
                        </p>
                        <p className="text-gray-700 mb-2">
                            <strong>{t('common.updatedAt')}:</strong> {order.updatedAt}
                        </p>
                        <p className="text-gray-700 mb-2">
                            <strong>{t('common.updatedBy')}:</strong> {getUserFullName(order.updatedBy, users)}
                        </p>
                        <p className="text-gray-700 mb-4">
                            <strong>{t('order.totalAmount')}:</strong> {formatPrice(order.totalPrice, order.currency)}
                        </p>
                        <div className="mb-4">
                            <label className="text-gray-700 block text-sm font-medium mb-1">{t('order.title')} {t('product.currency')}</label>
                            {isOrderDone ? (
                                <p className="text-gray-900 p-2">{currency || 'N/A'}</p>
                            ) : (
                                <select 
                                    value={currency || ''} 
                                    onChange={(e) => setCurrency(e.target.value)}
                                    className="border p-1 rounded w-full"
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="EUR">EUR</option>
                                    <option value="RSD">RSD</option>
                                </select>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="text-gray-700 block text-sm font-medium mb-1">{t('order.title')} Bank Client</label>
                            {isOrderDone ? (
                                <div>
                                    <p className="text-gray-900 p-2">{bankClient || 'N/A'}</p>
                                    {bankClient === 'CUSTOM' && (
                                        <div className="mt-2 space-y-2 bg-gray-50 p-3 rounded">
                                            <p className="text-sm"><strong>Bank {t('common.name')}:</strong> {customCurrency?.bankName || 'N/A'}</p>
                                            <p className="text-sm"><strong>{t('calculation.amount')}:</strong> {customCurrency?.amount || 0}</p>
                                        </div>
                                    )}
                                    {bankClient && bankClient !== 'CUSTOM' && order?.currencyInfo && (
                                        <div className="mt-2 space-y-2 bg-gray-50 p-3 rounded">
                                            <p className="text-sm"><strong>{t('common.date')}:</strong> {order.currencyInfo.createdAtDate || order.currencyInfo.displayDate || 'N/A'}</p>
                                            <p className="text-sm"><strong>Rate:</strong> {order.currencyInfo.middle || order.currencyInfo.rate || order.currencyInfo.amount || 'N/A'}</p>
                                            {order.currencyInfo.buy && (
                                                <p className="text-sm"><strong>Buy:</strong> {order.currencyInfo.buy}</p>
                                            )}
                                            {order.currencyInfo.sell && (
                                                <p className="text-sm"><strong>Sell:</strong> {order.currencyInfo.sell}</p>
                                            )}
                                            {order.currencyInfo.fromCurrency && order.currencyInfo.toCurrency && (
                                                <p className="text-sm"><strong>{t('product.currency')}:</strong> {order.currencyInfo.fromCurrency} → {order.currencyInfo.toCurrency}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <select 
                                        value={bankClient || ''}
                                        onChange={(e) => {
                                            setBankClient(e.target.value);
                                            if (e.target.value === 'CUSTOM' || !e.target.value) {
                                                setCurrencyHistoryId(null);
                                            }
                                        }}
                                        className="border p-1 rounded w-full"
                                        disabled={isLoadingCurrencies}
                                    >
                                        <option value="">Select</option>
                                        {availableBankClients.map(client => (
                                            <option key={client} value={client}>
                                                {client.replace('_', ' ')}
                                            </option>
                                        ))}
                                        <option value="CUSTOM">Custom</option>
                                    </select>

                                    {bankClient === 'CUSTOM' && (
                                        <div className="mt-4 space-y-2">
                                            <input
                                                type="text"
                                                value={customCurrency?.bankName || ''}
                                                onChange={(e) => setCustomCurrency(prev => ({
                                                    ...prev,
                                                    bankName: e.target.value
                                                }))}
                                                placeholder={`Bank ${t('common.name')}`}
                                                className="border p-2 rounded w-full"
                                                disabled={!order}
                                            />
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={customCurrency.amount}
                                                onChange={(e) => setCustomCurrency(prev => ({
                                                    ...prev,
                                                    amount: parseFloat(e.target.value) || 0
                                                }))}
                                                placeholder={t('calculation.amount')}
                                                className="border p-2 rounded w-full"
                                            />
                                        </div>
                                    )}

                                    {bankClient && bankClient !== 'CUSTOM' && (
                                        <div className="mt-4">
                                            <label className="text-gray-700 block text-sm font-medium mb-1">{t('product.currency')} {t('common.date')} (EUR)</label>
                                            {isLoadingCurrencies ? (
                                                <div className="text-sm text-gray-500">{t('common.loading')}...</div>
                                            ) : availableDates.length > 0 ? (
                                                <select
                                                    value={currencyHistoryId || ''}
                                                    onChange={(e) => setCurrencyHistoryId(e.target.value ? parseInt(e.target.value) : null)}
                                                    className="border p-1 rounded w-full"
                                                >
                                                    <option value="">{t('common.select')} {t('common.date')}</option>
                                                    {availableDates.map(date => (
                                                        <option key={date.id} value={date.id}>
                                                            {date.displayDate} (Rate: {date.rate})
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className="text-sm text-gray-500 italic">{t('common.noData')}</div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="text-gray-700 block text-sm font-medium mb-1">{t('order.title')} {t('common.status')}</label>
                            {allowedStatuses.length > 0 ? (
                                <select 
                                value={orderStatus || ''} 
                                onChange={handleOrderStatusChange}
                                className="border p-2 rounded w-full"
                                disabled={!order}
                            >
                                <option value="" disabled>{t('common.select')} {t('common.status')}</option>
                                {order?.status && (
                                    <option value={order.status} disabled>
                                        {order.status.replace("_", " ")}
                                    </option>
                                )}
                                {allowedStatuses.map((status) => (
                                    <option key={status} value={status}>
                                        {status.replace("_", " ")}
                                    </option>
                                ))}
                            </select>
                            ) : (
                                <div className={`text-sm font-medium px-3 py-2 rounded ${statusColors[order.status]}`}>
                                    {order.status.replace("_", " ")}
                                </div>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">{t('order.customer')}</label>
                            {isOrderDone ? (
                                <p className="text-gray-900 p-2">{customerName || 'N/A'}</p>
                            ) : (
                                <CustomerAutoComplete value={customerName} onChange={setCustomerInfo}/>
                            )}
                        </div>

                        {/* For Company Field */}
                        {orderStatus === 'IN_PROGRESS' && (
                            <div className="mb-4">
                                <div className="flex items-center mb-2">
                                    <input
                                        type="checkbox"
                                        id="overrideForCompany"
                                        checked={overrideForCompany}
                                        onChange={(e) => {
                                            setOverrideForCompany(e.target.checked);
                                            if (!e.target.checked) {
                                                setForCompanyValue('');
                                                setShowUserSuggestions(false);
                                            }
                                        }}
                                        className="mr-2"
                                    />
                                    <label htmlFor="overrideForCompany" className="text-sm font-medium">
                                        {t('order.overrideForCompany')}
                                    </label>
                                </div>
                                {overrideForCompany && (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={forCompanyValue}
                                            onChange={handleForCompanyChange}
                                            onFocus={() => {
                                                if (forCompanyValue.trim() && filteredUsers.length > 0) {
                                                    setShowUserSuggestions(true);
                                                }
                                            }}
                                            placeholder={t('order.selectUser')}
                                            className="border p-2 rounded w-full"
                                        />
                                        {showUserSuggestions && filteredUsers.length > 0 && (
                                            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                                                {filteredUsers.map(user => {
                                                    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                                                    return (
                                                        <div
                                                            key={user.id}
                                                            onClick={() => handleUserSelect(user)}
                                                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                                        >
                                                            <div className="font-medium">{fullName || user.username}</div>
                                                            {user.email && (
                                                                <div className="text-sm text-gray-500">{user.email}</div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Display For Company in other statuses */}
                        {orderStatus !== 'IN_PROGRESS' && order?.forCompany && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">{t('order.forCompany')}</label>
                                <p className="text-gray-900 p-2 bg-gray-50 rounded">{order.forCompany}</p>
                            </div>
                        )}
                    </div>

                    {/* Right column - Additional Info */}
                    {orderStatus !== 'OPEN' && (
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-2 text-gray-700">{t('order.additionalInfo')}</h3>
                            {isOrderDone ? (
                                <div className="mt-4 space-y-2 bg-gray-50 p-4 rounded">
                                    <p className="text-sm"><strong>{t('order.validityOfTheOffer')}:</strong> {validityOfTheOffer || 'N/A'}</p>
                                    <p className="text-sm"><strong>{t('order.deliveryTime')}:</strong> {deliveryTimeFrom || 'N/A'} - {deliveryTimeTo || 'N/A'} days</p>
                                    <p className="text-sm"><strong>{t('order.placeOfDelivery')}:</strong> {placeOfDelivery || 'N/A'}</p>
                                    <p className="text-sm"><strong>{t('order.warranty')}:</strong> {warranty || 'N/A'}</p>
                                </div>
                            ) : (
                                <div className="mt-4 flex flex-wrap gap-4">
                                    <input type="text" value={validityOfTheOffer}
                                           onChange={(e) => setValidityOfTheOffer(e.target.value)}
                                           placeholder={t('order.validityOfTheOffer')} className="border p-2 rounded w-64"/>
                                    <input type="number" value={deliveryTimeFrom}
                                           onChange={(e) => setDeliveryTimeFrom(e.target.value)} placeholder={`${t('order.deliveryDate')} From`}
                                           className="border p-2 rounded w-40"/>
                                    <input type="number" value={deliveryTimeTo}
                                           onChange={(e) => setDeliveryTimeTo(e.target.value)} placeholder={`${t('order.deliveryDate')} To`}
                                           className="border p-2 rounded w-40"/>
                                    <input type="text" value={placeOfDelivery}
                                           onChange={(e) => setPlaceOfDelivery(e.target.value)}
                                           placeholder={t('order.placeOfDelivery')} className="border p-2 rounded w-64"/>
                                    <input type="text" value={warranty} onChange={(e) => setWarranty(e.target.value)}
                                           placeholder={t('order.warranty')} className="border p-2 rounded w-64"/>
                                </div>
                            )}
                            <br/>
                            {order?.status !== 'CLOSED' && (
                                <div className="mt-6 flex flex-wrap gap-3">
                                    <button
                                        onClick={() => dispatch(downloadOrderPdfRequest(order.id, 'DEFAULT'))}
                                        className="bg-[#34495e] hover:bg-[#2c3e50] text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                                        title={t('order.generatePDF')}
                                    >
                                        {t('order.generatePDF')}
                                    </button>

                                    <button
                                        onClick={() => dispatch(downloadOrderPdfRequest(order.id, 'OLD'))}
                                        className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                                        title={`${t('order.generatePDF')} (Legacy)`}
                                    >
                                        {t('order.generatePDF')} (Legacy)
                                    </button>

                                    {order?.status === 'IN_PROGRESS' && (
                                        <button
                                            onClick={handleSaveProformaInvoice}
                                            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                                            title={t('order.saveProforma')}
                                        >
                                            {t('order.saveProforma')}
                                        </button>
                                    )}

                                    {order?.status !== 'OPEN' && (
                                        <button
                                            onClick={handleDownloadProformaInvoicesZip}
                                            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                                            title={t('order.downloadAllProformas')}
                                        >
                                            {t('order.downloadPDF')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {!isOrderDone && (
                    <button onClick={handleOrderDetailsUpdate}
                            className="mt-6 bg-[#34495e] hover:bg-[#2c3e50] text-white font-medium py-2.5 px-8 rounded-md shadow-sm transition-colors"
                            title={t('common.save')}>
                        {t('common.save')}
                    </button>
                )}
            </div>

            <ProductSnapshotsSection existingSnapshots={existingSnapshots} order={order} error={error}/>
        </div>
    );
};

export default OrderDetailsSection;
